import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Grid, Stack, Switch, TextField, Theme, Typography, useTheme } from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import BackNavigationButton from '../../../components/common/BackNavigationButton';
import LargeMaterialUISwitch from '../../../components/common/LargeMaterialUISwitch';
import PageContainer from '../../../components/common/PageContainter';
import StyledCard from '../../../components/common/StyledCard';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import SextforceLiveStreamAutoAnnouncementsGroupRow from '../../../components/services/hostess/liveStreams/autoAnnouncements/SextforceLiveStreamAutoAnnouncementsGroupRow';
import SextforceLiveStreamAutoAnnouncementsMessageGroupForm from '../../../components/services/hostess/liveStreams/autoAnnouncements/SextforceLiveStreamAutoAnnouncementsMessageGroupForm';
import useSextforceLiveStreamAutoAnnouncementsMessages, {
    OnlyFansSextfoceLiveStreamAnnoncementMessageGroup,
    OnlyFansSextfoceLiveStreamAnnouncementMessage,
} from '../../../hooks/useSextforceLiveStreamAutoAnnouncementsMessages';
import useSubscriber, { OnlyFansSextforceLiveStreamAutoAnnoncement, OnlyFansSubscriber } from '../../../hooks/useSubscriber';
import { SettingsContext } from '../../../store/SettingsContext';

const SextforceLiveStreamAutoAnnouncements = () => {
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();

    const [active, setActive] = useState<boolean>(true);

    // Message drag state
    const [messageDragState, setMessageDragState] = useState<{
        draggedIndex: number | null;
        draggedOverIndex: number | null;
        groupId: string | null;
    }>({
        draggedIndex: null,
        draggedOverIndex: null,
        groupId: null,
    });

    // Add state to track what's being dragged
    const [currentDragType, setCurrentDragType] = useState<'group' | 'message' | null>(null);
    const [currentDragGroupId, setCurrentDragGroupId] = useState<string | null>(null);

    const { data: subscriber, isLoading: isSubscriberLoading, setServiceRunningLoading, setServiceSettings } = useSubscriber();

    // Updated settings state to match new interface (removed global interval fields)
    const [settings, setSettings] = useState<OnlyFansSextforceLiveStreamAutoAnnoncement>({
        active: false,
        announceTopTippers: {
            active: false,
            topTippersCount: 10,
            announcementIntervalMinutes: 3,
        },
    });

    const {
        data: liveStreamAutoAnnouncementsGroups,
        isLoading: isLiveStreamAutoAnnouncementsMessagesLoading,
        refetch: refetchLiveStreamAutoAnnouncementsMessages,
        sextforceLiveStreamAutoAnnouncementMessageGroupCreate,
        isSextforceAutoAnnouncementMessageGroupCreateLoading,
        sextforceLiveStreamAutoAnnouncementMessageSortOrderUpdate,
        isSextforceAutoAnnouncementMessageSortOrderUpdateLoading,
    } = useSextforceLiveStreamAutoAnnouncementsMessages();

    const [showCreateGroupDialog, setShowCreateGroupDialog] = useState<boolean>(false);

    const handleUpdateSettings = useCallback(
        (newSettings: OnlyFansSextforceLiveStreamAutoAnnoncement) => {
            setServiceSettings('sextforce.liveStream.autoAnnouncement', newSettings)
                .then(settings => {
                    setSettings(settings);
                    enqueueSnackbar('Live Stream Auto Announcements settings updated successfully', {
                        variant: 'success',
                        autoHideDuration: 3000,
                    });
                })
                .catch(error => {
                    enqueueSnackbar('Failed to update Live Stream Auto Announcements settings', {
                        variant: 'error',
                        autoHideDuration: 3000,
                    });
                });
        },
        [setServiceSettings, enqueueSnackbar, dialog],
    );

    const handleMessageOrderChange = useCallback(
        (groupId: string, newOrderedMessages: OnlyFansSextfoceLiveStreamAnnouncementMessage[]) => {
            const newSortOrder = newOrderedMessages.map((message, index) => ({
                messageId: message._id!,
                sortOrder: index,
            }));
            sextforceLiveStreamAutoAnnouncementMessageSortOrderUpdate(groupId, newSortOrder)
                .then(() => {
                    enqueueSnackbar('Live Stream Auto Announcement Messages order updated successfully', {
                        variant: 'success',
                        autoHideDuration: 3000,
                    });
                    refetchLiveStreamAutoAnnouncementsMessages();
                })
                .catch(error => {
                    enqueueSnackbar('Failed to update Live Stream Auto Announcement Messages order', {
                        variant: 'error',
                        autoHideDuration: 3000,
                    });
                });
        },
        [sextforceLiveStreamAutoAnnouncementMessageSortOrderUpdate, enqueueSnackbar, refetchLiveStreamAutoAnnouncementsMessages],
    );

    const throttledMessageDragOver = useCallback(
        (index: number, groupId: string) => {
            if (
                currentDragType === 'message' &&
                currentDragGroupId === groupId &&
                messageDragState.groupId === groupId &&
                messageDragState.draggedOverIndex !== index
            ) {
                setMessageDragState(prev => ({ ...prev, draggedOverIndex: index }));
            }
        },
        [messageDragState.groupId, messageDragState.draggedOverIndex, currentDragType, currentDragGroupId],
    );

    // Message drag handlers
    const handleMessageDragStart = useCallback((e: React.DragEvent, index: number, type: 'message', groupId: string) => {
        setCurrentDragType('message');
        setCurrentDragGroupId(groupId);
        setMessageDragState({
            draggedIndex: index,
            draggedOverIndex: null,
            groupId: groupId,
        });
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', '');
    }, []);

    const handleMessageDragEnd = useCallback((e: React.DragEvent) => {
        setCurrentDragType(null);
        setCurrentDragGroupId(null);
        setMessageDragState({
            draggedIndex: null,
            draggedOverIndex: null,
            groupId: null,
        });
    }, []);

    const handleMessageDragOver = useCallback(
        (e: React.DragEvent, index: number, type: 'message', groupId: string) => {
            e.preventDefault();
            // Only handle message drag over within the same group
            if (currentDragType === 'message' && currentDragGroupId === groupId && messageDragState.groupId === groupId) {
                e.dataTransfer.dropEffect = 'move';
                throttledMessageDragOver(index, groupId);
            } else {
                e.dataTransfer.dropEffect = 'none';
            }
        },
        [messageDragState.groupId, throttledMessageDragOver, currentDragType, currentDragGroupId],
    );

    const handleMessageDrop = useCallback(
        (e: React.DragEvent, dropIndex: number, type: 'message', groupId: string) => {
            e.preventDefault();

            // Only handle message drops within the same group
            if (currentDragType !== 'message' || currentDragGroupId !== groupId) {
                return;
            }

            if (
                messageDragState.draggedIndex === null ||
                messageDragState.draggedIndex === dropIndex ||
                messageDragState.groupId !== groupId ||
                !liveStreamAutoAnnouncementsGroups
            ) {
                return;
            }

            const group = liveStreamAutoAnnouncementsGroups.find(g => g._id === groupId);
            if (!group || !group.messages) {
                return;
            }

            const newMessages = [...group.messages];
            const draggedMessage = newMessages[messageDragState.draggedIndex];

            newMessages.splice(messageDragState.draggedIndex, 1);
            newMessages.splice(dropIndex, 0, draggedMessage);

            handleMessageOrderChange(groupId, newMessages);
            setMessageDragState({
                draggedIndex: null,
                draggedOverIndex: null,
                groupId: null,
            });
        },
        [messageDragState, liveStreamAutoAnnouncementsGroups, handleMessageOrderChange, currentDragType, currentDragGroupId],
    );

    useEffect(() => {
        if (subscriber) {
            const sub: OnlyFansSubscriber = subscriber as OnlyFansSubscriber;

            setActive(sub.sextforce?.liveStream?.autoAnnouncement?.active || false);

            setSettings({
                active: sub.sextforce?.liveStream?.autoAnnouncement?.active || false,
                announceTopTippers: {
                    active: sub.sextforce?.liveStream?.autoAnnouncement?.announceTopTippers?.active || false,
                    topTippersCount: sub.sextforce?.liveStream?.autoAnnouncement?.announceTopTippers?.topTippersCount || 10,
                    announcementIntervalMinutes:
                        sub.sextforce?.liveStream?.autoAnnouncement?.announceTopTippers?.announcementIntervalMinutes || 3,
                    lastAnnouncementDate:
                        sub.sextforce?.liveStream?.autoAnnouncement?.announceTopTippers?.lastAnnouncementDate || undefined,
                },
                // Removed announcementEveryAmount and announcementEveryUnit
            });
        } else {
            setActive(false);
        }
    }, [subscriber]);

    const handleCreateGroup = useCallback(
        (group: OnlyFansSextfoceLiveStreamAnnoncementMessageGroup) => {
            sextforceLiveStreamAutoAnnouncementMessageGroupCreate(group)
                .then(newGroup => {
                    if (newGroup && newGroup._id) {
                        enqueueSnackbar('Live Stream Auto Announcement Group created successfully', {
                            variant: 'success',
                            autoHideDuration: 3000,
                        });
                        setShowCreateGroupDialog(false);
                        refetchLiveStreamAutoAnnouncementsMessages();
                    } else {
                        enqueueSnackbar('Failed to create Live Stream Auto Announcement Group', {
                            variant: 'error',
                            autoHideDuration: 3000,
                        });
                    }
                })
                .catch(error => {
                    enqueueSnackbar('Failed to create Live Stream Auto Announcement Group', {
                        variant: 'error',
                        autoHideDuration: 3000,
                    });
                });
        },
        [sextforceLiveStreamAutoAnnouncementMessageGroupCreate, enqueueSnackbar, refetchLiveStreamAutoAnnouncementsMessages],
    );

    // Memoize the group list to prevent unnecessary re-renders
    const groupRows = useMemo(() => {
        if (!liveStreamAutoAnnouncementsGroups || liveStreamAutoAnnouncementsGroups.length === 0) {
            return null;
        }

        return liveStreamAutoAnnouncementsGroups.map((group, index) => (
            <SextforceLiveStreamAutoAnnouncementsGroupRow
                key={group._id}
                subscriber={subscriber as OnlyFansSubscriber}
                group={group}
                onMessageDragStart={handleMessageDragStart}
                onMessageDragEnd={handleMessageDragEnd}
                onMessageDragOver={handleMessageDragOver}
                onMessageDrop={handleMessageDrop}
                messageDragState={messageDragState}
                currentDragType={currentDragType}
                currentDragGroupId={currentDragGroupId}
            />
        ));
    }, [
        liveStreamAutoAnnouncementsGroups,
        subscriber,
        messageDragState,
        currentDragType,
        currentDragGroupId,
        handleMessageDragStart,
        handleMessageDragEnd,
        handleMessageDragOver,
        handleMessageDrop,
    ]);

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        <BackNavigationButton url={`/subscribers/${subscriber._id}/${settingsContext.services.sextforce.homeUrl}`} /> Live
                        Stream Auto Announcements for {subscriber.username}
                    </Typography>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Live Stream Auto Announcements" />
                    </StyledCard>
                    <StyledCard>
                        <Typography variant="body1" gutterBottom>
                            The system monitors your live streams and automatically posts periodic announcements inside your live stream
                            chat. Each group of messages can have its own timing interval, allowing you to post different types of
                            announcements at different frequencies.
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Use this feature to remind your viewers to tip you, subscribe, or purchase content during the live stream by
                            posting a menu of available options.
                        </Typography>
                    </StyledCard>

                    {/* Main switch and settings components remain the same */}
                    <StyledCard>
                        <Stack direction="column" spacing={1}>
                            <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                                <Grid item xs>
                                    Live Stream Auto Announcements is currently{' '}
                                    {active ? (
                                        <span style={{ color: theme.palette.success.dark }}>running</span>
                                    ) : (
                                        <span style={{ color: theme.palette.error.main }}>not running</span>
                                    )}
                                    .
                                </Grid>
                                <Grid item xs="auto">
                                    <LargeMaterialUISwitch
                                        checked={active}
                                        disabled={isSubscriberLoading || setServiceRunningLoading}
                                        onChange={e => {
                                            handleUpdateSettings({
                                                ...settings,
                                                active: e.target.checked,
                                            });
                                            setActive(e.target.checked);
                                        }}
                                        color="primary"
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                                <Grid item xs>
                                    <Stack direction="column">
                                        <Typography variant="body1">Auto announce top tippers</Typography>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            Enable to automatically announce your top tippers during live streams at regular intervals.
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs="auto">
                                    <Switch
                                        checked={settings.announceTopTippers?.active || false}
                                        disabled={isSubscriberLoading || setServiceRunningLoading}
                                        onChange={e => {
                                            handleUpdateSettings({
                                                ...settings,
                                                announceTopTippers: {
                                                    ...settings.announceTopTippers,
                                                    active: e.target.checked,
                                                },
                                            });
                                            setSettings(prev => ({
                                                ...prev,
                                                announceTopTippers: {
                                                    ...prev.announceTopTippers,
                                                    active: e.target.checked,
                                                },
                                            }));
                                        }}
                                        color="primary"
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                                <Grid item xs>
                                    <Stack direction="column">
                                        <Typography variant="body1">How often to announce top tippers</Typography>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            Set the interval at which top tippers are announced during live streams.
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs="auto">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <TextField
                                            type="number"
                                            size="small"
                                            variant="outlined"
                                            value={settings.announceTopTippers?.announcementIntervalMinutes || 3}
                                            onChange={e => {
                                                const newValue = parseInt(e.target.value, 10);
                                                handleUpdateSettings({
                                                    ...settings,
                                                    announceTopTippers: {
                                                        ...settings.announceTopTippers,
                                                        announcementIntervalMinutes: isNaN(newValue) ? 3 : newValue,
                                                    },
                                                });
                                                setSettings(prev => ({
                                                    ...prev,
                                                    announceTopTippers: {
                                                        ...prev.announceTopTippers,
                                                        announcementIntervalMinutes: isNaN(newValue) ? 3 : newValue,
                                                    },
                                                }));
                                            }}
                                            disabled={
                                                isSubscriberLoading ||
                                                setServiceRunningLoading ||
                                                !(settings.announceTopTippers?.active || false)
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                                        min.
                                                    </Typography>
                                                ),
                                            }}
                                            inputProps={{ min: 1, style: { textAlign: 'center', width: '40px' } }}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                                <Grid item xs>
                                    <Stack direction="column">
                                        <Typography variant="body1">Number of tippers to list</Typography>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            Set how many of your top tippers to announce during live streams.
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs="auto">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <TextField
                                            type="number"
                                            size="small"
                                            variant="outlined"
                                            value={settings.announceTopTippers?.topTippersCount || 10}
                                            onChange={e => {
                                                const newValue = parseInt(e.target.value, 10);
                                                handleUpdateSettings({
                                                    ...settings,
                                                    announceTopTippers: {
                                                        ...settings.announceTopTippers,
                                                        topTippersCount: isNaN(newValue) ? 10 : newValue,
                                                    },
                                                });
                                                setSettings(prev => ({
                                                    ...prev,
                                                    announceTopTippers: {
                                                        ...prev.announceTopTippers,
                                                        topTippersCount: isNaN(newValue) ? 10 : newValue,
                                                    },
                                                }));
                                            }}
                                            disabled={
                                                isSubscriberLoading ||
                                                setServiceRunningLoading ||
                                                !(settings.announceTopTippers?.active || false)
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                                        tippers
                                                    </Typography>
                                                ),
                                            }}
                                            inputProps={{
                                                min: 1,
                                                max: 10,
                                                style: { textAlign: 'center', width: '40px' },
                                            }}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Stack>
                    </StyledCard>

                    {/* {subscriber && (
                        <SextforceLiveStreamAutoAnnouncementsSettings
                            subscriber={subscriber as OnlyFansSubscriber}
                            isSubscriberLoading={isSubscriberLoading}
                            settings={subscriber.sextforce?.liveStream?.autoAnnouncement || settings}
                            setServiceRunningLoading={setServiceRunningLoading}
                            handleUpdateSettings={handleUpdateSettings}
                        />
                    )} */}

                    <StyledCard noCard>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            startIcon={<AddIcon />}
                            fullWidth
                            onClick={() => {
                                setShowCreateGroupDialog(true);
                            }}
                            disabled={isSubscriberLoading || setServiceRunningLoading}
                        >
                            Create New Announcement Group
                        </Button>
                    </StyledCard>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title={`Announcement Groups`} isLoading={isSubscriberLoading} />
                    </StyledCard>

                    <StyledCard sx={{ pl: 0, pr: 0 }}>
                        <Box sx={{ pr: 2, pl: 2, pb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Live Stream Auto Announcement Groups
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Organize your announcement messages into groups. Each group has its own timing interval and contains
                                multiple messages that will be rotated during live streams. Configure how often each group announces by
                                editing the group settings. Use the drag handles to reorder groups and messages within groups.
                            </Typography>
                        </Box>
                        <Stack direction="column" spacing={0}>
                            {!showCreateGroupDialog &&
                                !isLiveStreamAutoAnnouncementsMessagesLoading &&
                                liveStreamAutoAnnouncementsGroups &&
                                liveStreamAutoAnnouncementsGroups.length === 0 && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',
                                            color: theme.palette.text.secondary,
                                            p: 2,
                                        }}
                                    >
                                        No live stream auto announcement groups found. Create your first group to get started.
                                    </Typography>
                                )}
                            {!isLiveStreamAutoAnnouncementsMessagesLoading && groupRows}
                        </Stack>
                    </StyledCard>

                    {showCreateGroupDialog && (
                        <SextforceLiveStreamAutoAnnouncementsMessageGroupForm
                            subscriber={subscriber as OnlyFansSubscriber}
                            onSubmit={handleCreateGroup}
                            isLoading={isSextforceAutoAnnouncementMessageGroupCreateLoading}
                            onCancel={() => {
                                setShowCreateGroupDialog(false);
                            }}
                            open={showCreateGroupDialog}
                        />
                    )}
                </>
            )}
        </PageContainer>
    );
};

export default SextforceLiveStreamAutoAnnouncements;
